// /app/frontend/src/contexts/AppSettingsContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { AppSettings, fetchAppSettings, loadDefaultAppSettings, loadDemoAppSettings } from '../utils/appSettings';

// Define the shape of the context value
interface AppSettingsContextValue {
  settings: AppSettings;
  appSettingsReady: boolean;
  error: string | null;
  loadSettings: () => void;
}

// Create the Context with an undefined default value
const AppSettingsContext = createContext<AppSettingsContextValue | undefined>(undefined);

// Define the props for the provider component
interface AppSettingsProviderProps {
  children: ReactNode;
  apiEndpoint?: string; // Optional: Custom API endpoint for fetching settings
}

// Provider component to supply AppSettings to the application
export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({
  children,
  apiEndpoint = '/api/frontend/settings/', // Default API endpoint
}) => {
  const [settings, setSettings] = useState<AppSettings>(loadDefaultAppSettings());
  const [appSettingsReady, setAppSettingsReady] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Define the fetch function with useCallback to prevent unnecessary re-creations
  const fetchSettingsAsync = useCallback(async () => {
    if (process.env.REACT_APP_DEMO_MODE === 'true') {
      console.log('Running in DEMO mode: using static app settings.');
      setSettings(loadDemoAppSettings());
      setAppSettingsReady(true);
      return;
    }

    setAppSettingsReady(false); // Set loading state
    try {
      const appSettings = await fetchAppSettings(apiEndpoint);
      setSettings(appSettings);
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Failed to load app settings:', err);
      setError(err.message || 'Failed to load app settings');
      // settings are already set to default
    } finally {
      setAppSettingsReady(true);
    }
  }, [apiEndpoint]);

  // Define the loadSettings method
  const loadSettings = useCallback(() => {
    fetchSettingsAsync();
  }, [fetchSettingsAsync]);

  return (
    <AppSettingsContext.Provider value={{ settings, appSettingsReady, error, loadSettings }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

// Custom hook to access the AppSettingsContext
export const useAppSettings = (): AppSettingsContextValue => {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
